import React, { useEffect } from "react";
import { useAppSelector, useAppDispatch } from "@/store";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Info } from "lucide-react";
import { reachedTerminal, stepChanged } from "@/app/booking-flow/publicBookingFlowSlice";
import { useGetIncomeGuidelinesQuery } from "@/app/booking-flow/publicBookingFlowApi";

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

/**
 * Display-only step, shown once a household size is on file. It quotes the monthly income limits
 * for that size and nothing else: deliberately no "you qualify" / "you do not qualify" verdict,
 * even though the previous step also collected a monthly income. Eligibility is determined at the
 * appointment, and a public page that appears to turn somebody away before staff have looked at
 * their case would keep applicants from booking at all.
 *
 * What it quotes follows the household (AB#595): the server lists only the programs its energy source
 * can receive, sets aside the ones the step-2 program check found already received this program year
 * (shown below the list with the reason), and, when none is left, says so instead of listing anything.
 * That last case is the same dead end as step 2's: it lands on the `hh_no_pledges` explanation, which
 * lets the hold go and offers Start Over. A program an administrator switched off (AB#607) is withheld
 * too, with its switch message as the reason; switches alone leaving nothing are not that dead end, so
 * the notes are shown and the booking itself is refused by the server.
 */
export function IncomeGuidelinesStep() {
  const dispatch = useAppDispatch();
  const householdData = useAppSelector(state => state.publicBookingFlow.householdData);
  const householdSize = householdData?.householdSize ?? 0;

  // The previous step will not advance without a valid size, so skipping the request here is a
  // guard against arriving with no state (a restored tab, a direct dispatch) rather than a case
  // the flow can reach normally.
  // Re-asked on every visit: the answer also depends on the session's stored step-2 program check,
  // which is not in the arguments, so a cached answer from before a corrected SSN would be stale.
  const { data, isLoading, isError } = useGetIncomeGuidelinesQuery(
    {
      householdSize,
      energySource: householdData?.energySource,
      pseService: householdData?.pseService,
    },
    { skip: householdSize < 1, refetchOnMountOrArgChange: true },
  );

  const noProgramRemains = !!data?.noProgramRemains;
  const noProgramRemainsMessage = data?.message ?? null;
  useEffect(() => {
    if (noProgramRemains) {
      dispatch(reachedTerminal({ type: "hh_no_pledges", detail: { message: noProgramRemainsMessage } }));
    }
  }, [noProgramRemains, noProgramRemainsMessage, dispatch]);
  const withheld = data && !noProgramRemains ? data.withheld ?? [] : [];

  const handleBack = () => {
    dispatch(stepChanged(3));
  };

  const handleContinue = () => {
    dispatch(stepChanged(5));
  };

  return (
    <Card className="w-full max-w-xl mx-auto border-none shadow-none sm:border-solid sm:shadow-sm">
      <CardHeader className="text-center px-4">
        <CardTitle className="text-2xl font-semibold text-slate-900">Income Guidelines</CardTitle>
        <CardDescription className="text-slate-500">
          {householdSize > 0
            ? `Monthly income limits for a household of ${householdSize}.`
            : "Monthly income limits by household size."}
        </CardDescription>
      </CardHeader>

      <CardContent>
        {isLoading && (
          <p className="text-sm text-slate-500 text-center py-6">Loading income guidelines…</p>
        )}

        {isError && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            We could not load the income guidelines just now. You can continue booking — the figures
            for your household will be confirmed at your appointment.
          </div>
        )}

        {/* Not when everything was withheld (AB#607: every program switched off): the notes below say why. */}
        {data && !noProgramRemains && data.programs.length === 0 && withheld.length === 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <Info className="w-4 h-4 shrink-0 mt-0.5" />
            Published income guidelines are not available for this household size. The figures for
            your household will be confirmed at your appointment.
          </div>
        )}

        {data && !noProgramRemains && data.programs.length > 0 && (
          <>
            <dl className="divide-y divide-slate-200 border-y border-slate-200">
              {data.programs.map(program => (
                <div key={program.programName} className="flex items-baseline justify-between py-3">
                  <dt className="text-slate-700">{program.programName}</dt>
                  <dd className="text-lg font-semibold text-slate-900 tabular-nums">
                    {currency.format(program.monthlyIncomeLimit)}
                    <span className="ml-1 text-sm font-normal text-slate-500">/ month</span>
                  </dd>
                </div>
              ))}
            </dl>

            <p className="mt-4 text-sm text-slate-500">
              Program year {data.programYear}. These figures are a guide only — your eligibility is
              confirmed at your appointment, and you are welcome to keep booking either way.
            </p>
          </>
        )}

        {withheld.length > 0 && (
          <ul className="mt-4 space-y-2" aria-label="Programs not offered">
            {withheld.map(program => (
              <li
                key={program.programName}
                className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
              >
                <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-500" />
                <span>
                  <span className="font-medium">{program.programName}</span>: {program.reason}
                </span>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mt-6 w-full max-w-md mx-auto">
          <Button type="button" variant="outline" className="flex flex-grow-1 text-base h-12" onClick={handleBack}>
            Back
          </Button>
          <Button
            type="button"
            className="flex flex-grow-2 text-base h-12"
            onClick={handleContinue}
          >
            Continue
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
